<script setup lang="ts">
import { type UseCollectionOptions } from '@/composables/useDataTableProps';
import { useUserCollection } from '@/composables/useUserCollection';
import { User, createNewUser } from '@/models/User';
import { watch, onMounted, ref, type Ref } from 'vue';
import KtgTable from '@kp/geofy-vuejs-lib/tg-system/components/KpTable.vue';
import type { KtgTableHeaderProps, KtgTableSortEntry } from '@kp/geofy-vuejs-lib/tg-system/components/KpTableTypes.js';
import { getI18n } from '@/helpers/i18n';
import { useRouter } from 'vue-router';
import { errToStr } from '@/helpers/tools';

const router = useRouter();
const props = withDefaults(
    defineProps<{
        /**
         * Initial record limit
         */
        initialLimit?: number;
        /**
         * Increase record limit by n records per click
         */
        increaseLimitBy?: number;
    }>(),
    {
        initialLimit: 20,
        increaseLimitBy: 20
    }
);

// Table properties like paging, sorting, filtering are stored
// in the Browser's Session storage, and restored on component mount:
const actPage: Ref<number> = ref(1);
const actPageSize: Ref<number> = ref(20);
const actTableSort: KtgTableSortEntry[] = [{ dataProperty: 'id', direction: 'ascending' } as KtgTableSortEntry];

const tableHeaders = ref<KtgTableHeaderProps[]>([
    {
        title: getI18n().t('id'),
        dataProperty: 'id',
        sortable: true
    },{
        title: getI18n().t('username'),
        dataProperty: 'username',
        sortable: true
    },
    {
        title: getI18n().t('name_vorname'),
        dataProperty: 'nachname_vorname',
        sortable: true
    },
    {
        title: getI18n().t('email'),
        dataProperty: 'email',
        sortable: true
    },
    {
        title: getI18n().t('gemeinde'),
        dataProperty: 'gemeinden',
        sortable: true
    },
    {
        title: getI18n().t('fachstelle'),
        dataProperty: 'fachstellen',
        sortable: true
    },
    {
        title: getI18n().t('admin'),
        dataProperty: 'ist_admin',
        sortable: true
    },
    {
        title: 'Aktionen',
        sortable: false,
        dataProperty: 'actions'
    }
]);

const searchQuery: Ref<string> = ref('');

const {
    models,
    query: queryUsers,
    collection,
    tableSort,
    pageSize,
    serverTotal
} = useUserCollection({
    autoload: true,
    page: actPage,
    pageSize: actPageSize,
    tableSort: actTableSort
} as UseCollectionOptions);

onMounted(async () => {
    await query();
});

watch(searchQuery, onSearchQueryChange);

async function query() {
    await queryUsers({
        // append the search field as query to the request:
        query: searchQuery?.value?.trim() || null
    });
}

function onSearchQueryChange(queryStr: string | undefined) {
    collection.setQueryParam('query', queryStr?.trim() || null);
    query();
}

async function addUser() {
    try {
        const newUser = await createNewUser();
        if (newUser?.id) {
            router.push({ name: 'admin-users-edit', params: { id: newUser.id } });
        }
    } catch (error) {
        console.error(error);
    }
}

async function onDeleteClick(user: User) {
    if (!user) {
        return;
    }
    const yes = confirm(getI18n().t('confirm_destroy'));
    if (yes) {
        try {
            await user.destroy();
            query();
        } catch (e) {
            alert(errToStr(e));
        }
    }
}
</script>
<template>
    <div class="flex justify-content-end align-items-center">
        <ktg-button class="mt-1rem mr-2rem" appearance="primary" size="small" trailing-icon="plus" @click="addUser">{{
            $t('add_user')
        }}</ktg-button>
        <ktg-field style="width: 40ch">
            <ktg-label for="my-gesuche-search-field">{{ $t('search_for') }}</ktg-label>
            <ktg-text-input
                class="w-full"
                v-model="searchQuery"
                type="text"
                :placeholder="$t('searchterm')"
                appearance="grey"
                trailing-icon="search"
            />
        </ktg-field>
    </div>
    <KtgTable v-model:sort="tableSort" v-model:pageSize="pageSize" :headers="tableHeaders" :data="models"
        ><template #[`item.gemeinden`]="{ item }">
            <div class="table-cell">{{ item.gemeinden }}</div></template
        ><template #[`item.actions`]="{ item }">
            <RouterLink custom :to="{ name: 'admin-users-edit', params: { id: item.id } }" v-slot="{ href, navigate }">
                <ktg-table-button :href="href" @click.prevent="navigate()">{{
                    $t('edit')
                }}</ktg-table-button></RouterLink
            ><br />
            <ktg-table-button icon="trash-delete" @click.prevent="onDeleteClick(item)">{{
                $t('delete')
            }}</ktg-table-button>
        </template></KtgTable
    >
    <div class="mt-2rem flex justify-content-end align-items-center">
        <span class="ktg-body-small">{{ Math.min(pageSize, serverTotal) }} {{ $t('von') }} {{ serverTotal }}</span>
        <ktg-button
            v-if="pageSize < serverTotal"
            appearance="tertiary"
            size="small"
            trailing-icon="plus"
            @click="pageSize = Math.min(pageSize + props.increaseLimitBy, serverTotal)"
        >
            {{ $t('show_n_more', { n: props.increaseLimitBy }) }}
        </ktg-button>
    </div>
</template>

<style scoped lang="scss">
.table-cell {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
